import React, { useState, useEffect, useCallback } from 'react';
import { currenciesApi } from '../../utils/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Admin.css';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ── Currency create/edit modal ─────────────────────────────────────── */
function CurrencyModal({ currency, onClose }) {
  const isEdit = !!currency;
  const [form, setForm] = useState({
    code: currency?.code || '',
    name: currency?.name || '',
    symbol: currency?.symbol || '',
    decimals: currency?.decimals ?? 2,
    isBase: currency?.isBase ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await currenciesApi.update(currency.id, { name: form.name, symbol: form.symbol, decimals: Number(form.decimals) });
      } else {
        await currenciesApi.create({ code: form.code, name: form.name, symbol: form.symbol, decimals: Number(form.decimals), isBase: form.isBase });
      }
      onClose(true);
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal">
        <div className="adm-modal-head">
          <h2>{isEdit ? `Edit — ${currency.code}` : 'Add Currency'}</h2>
          <button className="adm-modal-close" onClick={() => onClose(false)}><i className="fas fa-times"></i></button>
        </div>
        <div className="adm-modal-body">
          {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          {!isEdit && (
            <>
              <div className="adm-field">
                <label className="adm-label">ISO Code *</label>
                <input className="adm-input" value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())}
                  placeholder="e.g. USD" maxLength={3} autoFocus />
              </div>
              <div className="adm-field">
                <label className="adm-perm-item">
                  <input type="checkbox" checked={form.isBase} onChange={(e) => set('isBase', e.target.checked)} />
                  Base currency (local EGP equivalent — only one allowed)
                </label>
              </div>
            </>
          )}
          <div className="adm-field">
            <label className="adm-label">Name *</label>
            <input className="adm-input" value={form.name} onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. US Dollar" autoFocus={isEdit} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Symbol</label>
            <input className="adm-input" value={form.symbol} onChange={(e) => set('symbol', e.target.value)}
              placeholder="e.g. $" maxLength={8} style={{ maxWidth: 100 }} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Decimal Places</label>
            <input className="adm-input" type="number" value={form.decimals}
              onChange={(e) => set('decimals', e.target.value)} min={0} max={4} style={{ maxWidth: 80 }} />
          </div>
        </div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
          <button className="adm-btn adm-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <LoadingSpinner size="small" variant="button" /> : (isEdit ? 'Save Changes' : 'Add Currency')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Exchange-rates sub-panel ────────────────────────────────────────── */
function RatesPanel({ currency, onClose }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('currencies.manage');

  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rate, setRate] = useState('');
  const [rateDate, setRateDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRates(await currenciesApi.listRates(currency.id)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [currency.id]);

  useEffect(() => { load(); }, [load]);

  const addRate = async () => {
    if (!rate || isNaN(rate) || Number(rate) <= 0) { setError('Enter a valid positive rate.'); return; }
    setSaving(true); setError(null);
    try {
      await currenciesApi.addRate(currency.id, { rate: Number(rate), effectiveDate: rateDate });
      setRate('');
      load();
    } catch (e) { setError(e.message || 'Failed to add rate'); }
    finally { setSaving(false); }
  };

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal adm-modal--wide">
        <div className="adm-modal-head">
          <h2>Exchange Rates — {currency.code} / {currency.name}</h2>
          <button className="adm-modal-close" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>
        <div className="adm-modal-body">
          {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

          {canManage && (
            <div className="adm-rates-wrap" style={{ marginBottom: '1rem' }}>
              <p className="adm-rates-title">Add New Rate</p>
              <div className="adm-rates-row">
                <div className="adm-field" style={{ margin: 0 }}>
                  <label className="adm-label">Effective Date</label>
                  <input className="adm-input" type="date" value={rateDate} onChange={(e) => setRateDate(e.target.value)} />
                </div>
                <div className="adm-field" style={{ margin: 0 }}>
                  <label className="adm-label">Rate (1 {currency.code} = ? EGP)</label>
                  <input className="adm-input" type="number" step="0.0001" value={rate}
                    onChange={(e) => setRate(e.target.value)} placeholder="e.g. 49.75" />
                </div>
                <button className="adm-btn adm-btn--primary" onClick={addRate} disabled={saving}
                  style={{ marginTop: '1.25rem' }}>
                  {saving ? <LoadingSpinner size="small" variant="button" /> : <><i className="fas fa-plus"></i> Add</>}
                </button>
              </div>
            </div>
          )}

          {loading ? <LoadingSpinner variant="default" message="Loading rates…" /> : (
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Effective Date</th>
                  <th>Rate (EGP)</th>
                  <th>Added By</th>
                </tr>
              </thead>
              <tbody>
                {rates.length === 0 && <tr><td colSpan={3} className="adm-empty">No exchange rates recorded.</td></tr>}
                {rates.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.effectiveDate)}</td>
                    <td><strong style={{ fontFamily: 'var(--font-family-mono)' }}>{Number(r.rate).toFixed(4)}</strong></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{r.createdBy?.fullName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────── */
const CurrenciesPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('currencies.manage');

  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setCurrencies(await currenciesApi.list()); }
    catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModal = (refresh) => { setModal(null); if (refresh) load(); };

  return (
    <div className="adm-page">
      <div className="adm-header">
        <div>
          <h1 className="adm-title"><i className="fas fa-coins" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Currencies</h1>
          <p className="adm-subtitle">Multi-currency setup and exchange-rate history.</p>
        </div>
        {canManage && (
          <button className="adm-btn adm-btn--primary" onClick={() => setModal({ type: 'create' })}>
            <i className="fas fa-plus"></i> Add Currency
          </button>
        )}
      </div>

      {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      <div className="adm-card">
        {loading ? (
          <LoadingSpinner variant="default" message="Loading currencies…" />
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Symbol</th>
                  <th>Decimals</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currencies.length === 0 && (
                  <tr><td colSpan={6} className="adm-empty">No currencies found.</td></tr>
                )}
                {currencies.map((c) => (
                  <tr key={c.id}>
                    <td><code style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 700 }}>{c.code}</code></td>
                    <td>{c.name}</td>
                    <td style={{ fontFamily: 'var(--font-family-mono)' }}>{c.symbol || '—'}</td>
                    <td>{c.decimals}</td>
                    <td>
                      {c.isBase
                        ? <span className="adm-badge adm-badge--base">Base</span>
                        : <span className="adm-badge" style={{ background: 'var(--surface-3)', color: 'var(--text-muted)' }}>Foreign</span>}
                    </td>
                    <td>
                      <div className="adm-actions">
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" title="Exchange Rates"
                          onClick={() => setModal({ type: 'rates', currency: c })}>
                          <i className="fas fa-chart-line"></i> Rates
                        </button>
                        {canManage && (
                          <button className="adm-btn adm-btn--ghost adm-btn--sm" title="Edit"
                            onClick={() => setModal({ type: 'edit', currency: c })}>
                            <i className="fas fa-pen"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal?.type === 'create' && <CurrencyModal onClose={closeModal} />}
      {modal?.type === 'edit'   && <CurrencyModal currency={modal.currency} onClose={closeModal} />}
      {modal?.type === 'rates'  && <RatesPanel currency={modal.currency} onClose={() => setModal(null)} />}
    </div>
  );
};

export default CurrenciesPage;
